import { useEffect, useRef, useState } from "react";
import { storage } from "../../state/storage.js";
import { AssetImage } from "./AssetImage.js";
import { importAssetFromFile, releaseAsset } from "../../platform/assets/index.js";
import { useT } from "../../i18n/use-t.js";
import { createPersona, type Persona, type Settings } from "@roqeet/core";

/**
 * 페르소나 관리(결정 #22: 사용자 측 발신자). 페르소나는 @유저로 치환되고,
 * 채팅의 "user" 참가자 좌석을 뒷받침한다. 기본 페르소나는 새 대화에 자동 적용된다.
 *
 * 아바타는 콘텐츠 주소화 에셋(결정 #18), 저장은 storage 포트(결정 #20).
 */
export function PersonaSection() {
  const { t } = useT();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [editing, setEditing] = useState<Persona | "new" | null>(null);
  const [settings, setSettings] = useState<Settings | undefined>(undefined);

  const reload = async () => {
    const [p, s] = await Promise.all([storage.personas.list(), storage.getSettings()]);
    setPersonas(p);
    setSettings(s);
  };
  
  useEffect(() => {
    void reload();
  }, []);

  const setDefault = async (target: Persona) => {
    const currentSettings = await storage.getSettings();
    if (currentSettings) {
      const next = { ...currentSettings, defaultPersonaId: target.id };
      await storage.putSettings(next);
      setSettings(next);
    }
  };

  const remove = async (p: Persona) => {
    if (p.avatarAssetId) await releaseAsset(p.avatarAssetId).catch(() => {});
    await storage.personas.delete(p.id);
    await reload();
  };

  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("persona.title")}
      </h2>
      <div className="space-y-2">
        <ul className="space-y-2">
          {personas.map((p) => {
            const isDefault = settings?.defaultPersonaId === p.id;
            return (
              <li
                key={p.id}
                className="flex items-center gap-3 rounded-xl border border-line bg-bg-soft px-3 py-2"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-line">
                  <AssetImage
                    assetId={p.avatarAssetId}
                    alt={p.name}
                    className="h-full w-full object-cover"
                    fallback={<span className="text-sm">{p.name.slice(0, 1)}</span>}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-ink">{p.name}</span>
                    {isDefault && (
                      <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs text-accent-soft">
                        {t("common.default")}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-ink-faint">
                    {p.description || t("persona.noDescription")}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!isDefault && (
                    <button
                      className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                      onClick={() => setDefault(p)}
                    >
                      {t("common.default")}
                    </button>
                  )}
                  <button
                    className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
                    onClick={() => setEditing(p)}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    className="rounded-lg border border-line px-2 py-1 text-xs text-red-300 hover:bg-bg-raised disabled:opacity-40"
                    onClick={() => remove(p)}
                    disabled={personas.length <= 1}
                    title={personas.length <= 1 ? t("persona.keepOne") : t("common.delete")}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {editing ? (
          <PersonaForm
            persona={editing === "new" ? undefined : editing}
            onDone={async () => {
              setEditing(null);
              await reload();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <button
            className="w-full rounded-xl border border-dashed border-line px-3 py-2 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={() => setEditing("new")}
          >
            {t("persona.add")}
          </button>
        )}
      </div>
    </section>
  );
}

function PersonaForm({
  persona,
  onDone,
  onCancel,
}: {
  persona?: Persona;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [name, setName] = useState(persona?.name ?? "");
  const [description, setDescription] = useState(persona?.description ?? "");
  const [avatarAssetId, setAvatarAssetId] = useState(persona?.avatarAssetId);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const onAvatarChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const asset = await importAssetFromFile(file);
      if (avatarAssetId) await releaseAsset(avatarAssetId).catch(() => {});
      setAvatarAssetId(asset.id);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (persona) {
        await storage.personas.put({
          ...persona,
          name: name.trim(),
          description,
          ...(avatarAssetId ? { avatarAssetId } : {}),
        });
      } else {
        const existing = await storage.personas.list();
        const p = createPersona({
          name: name.trim(),
          description,
          ...(avatarAssetId ? { avatarAssetId } : {}),
        });
        await storage.personas.put(p);
        
        if (existing.length === 0) {
          const currentSettings = await storage.getSettings();
          if (currentSettings) {
            await storage.putSettings({ ...currentSettings, defaultPersonaId: p.id });
          }
        }
      }
      await onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-xl border border-line bg-bg-soft p-3">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatarChosen} />
      <div className="flex items-center gap-3">
        <button
          className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-line"
          onClick={() => fileRef.current?.click()}
          title={t("persona.avatarChange")}
        >
          <AssetImage
            assetId={avatarAssetId}
            alt={name || t("persona.avatarAlt")}
            className="h-full w-full object-cover"
            fallback={<span>{name.slice(0, 1) || "?"}</span>}
          />
        </button>
        <input
          className="flex-1 rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
          placeholder={t("persona.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <textarea
        className="w-full resize-y rounded-lg border border-line bg-bg-raised px-2 py-1.5 text-sm text-ink outline-none focus:border-accent"
        rows={3}
        placeholder={t("persona.descPlaceholder")}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <div className="flex justify-end gap-2">
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
          onClick={onCancel}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
          onClick={save}
          disabled={busy || !name.trim()}
        >
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
